/**
 * For now we do this by hand however these types should be generated for us.
 *
 * Supported queries are driven by the page folder structure and names used. They are then populated based
 * on route fragements provided by the user. Every level of folder structure added which has dynamic fields
 * should in tern produce its type below. The same goes for query string parameters consumed.
 */

import { SpecificationVersionInfo } from '@squareup/dex-types-oas-path';

import { serializeSpecPathParam } from './specification-path-parser';

// L1
type SquareVersionPageQuery = { square_version: string };

// L2
type ApiPageQuery = SquareVersionPageQuery & { api: string };
type ObjectPageQuery = SquareVersionPageQuery & {
  object: string;
};
type EnumPageQuery = SquareVersionPageQuery & {
  enum: string;
};
type WebhookPageQuery = SquareVersionPageQuery & {
  webhook: string;
};

// L3
type ApiEndpointPageQuery = ApiPageQuery & {
  endpoint: string;
  env?: 'sandbox' | 'production';
  prefill?: string;
  appId?: string;
};
type ApiWebhookPageQuery = ApiPageQuery & WebhookPageQuery;

/**
 * Path generation helpers
 */

/**
 * Takes an entities type and letter and returns its index value.
 */
function entityToIndex(type: string, letter: string) {
  return `${type}-index-${letter}`;
}

function versionInfoToVersionPath(
  versionInfo:
    | Pick<SpecificationVersionInfo, 'version' | 'namespace'>
    | undefined
) {
  let path: string | undefined = undefined;
  if (versionInfo) {
    const { version, namespace } = versionInfo;
    if (!version) {
      // In cases where a version does not exist try to use the namespace. This can happen for the 'square' namespace as there is no version provided.
      path = namespace;
    } else {
      path = serializeSpecPathParam(namespace, version);
    }
  }

  return path;
}

/**
 * Paths
 */

enum ReferencePath {
  VERSION = '/reference/[square_version]',
  VERSION_OBJECTS = '/reference/[square_version]/objects',
  VERSION_OBJECT = '/reference/[square_version]/objects/[object]',
  VERSION_ENUMS = '/reference/[square_version]/enums',
  VERSION_ENUM = '/reference/[square_version]/enums/[enum]',
  VERSION_WEBHOOKS = '/reference/[square_version]/webhooks',
  VERSION_WEBHOOKS_WEBHOOK = '/reference/[square_version]/webhooks/[webhook]',
  VERSION_DEPRECATED = '/reference/[square_version]/deprecated',
  VERSION_DEPRECATED_WEBHOOKS = '/reference/[square_version]/deprecated/webhooks',
  VERSION_API = '/reference/[square_version]/[api]',
  VERSION_API_ENDPOINT = '/reference/[square_version]/[api]/[endpoint]',
  VERSION_API_WEBHOOK = '/reference/[square_version]/[api]/webhooks/[webhook]',
  VERSION_API_WEBHOOK_INDEX = '/reference/[square_version]/[api]/webhooks',
  VERSION_EXPLORER = '/explorer/[square_version]',
  VERSION_EXPLORER_ENDPOINT = '/explorer/[square_version]/[api]/[endpoint]',
}

/**
 * Hrefs
 */

/**
 * Reference builders which can be used to build the href for a page.
 */
const ReferenceHrefBuilders = {
  version: (query: SquareVersionPageQuery) => ({
    pathname: ReferencePath.VERSION,
    query,
  }),
  versionObjects: (query: SquareVersionPageQuery) => ({
    pathname: ReferencePath.VERSION_OBJECTS,
    query,
  }),
  versionObject: (query: ObjectPageQuery) => ({
    pathname: ReferencePath.VERSION_OBJECT,
    query,
  }),
  versionEnums: (query: SquareVersionPageQuery) => ({
    pathname: ReferencePath.VERSION_ENUMS,
    query,
  }),
  versionEnum: (query: EnumPageQuery) => ({
    pathname: ReferencePath.VERSION_ENUM,
    query,
  }),
  versionDeprecated: (query: SquareVersionPageQuery) => ({
    pathname: ReferencePath.VERSION_DEPRECATED,
    query,
  }),
  versionDeprecatedWebhooks: (query: SquareVersionPageQuery) => ({
    pathname: ReferencePath.VERSION_DEPRECATED_WEBHOOKS,
    query,
  }),
  versionWebhooks: (query: SquareVersionPageQuery) => ({
    pathname: ReferencePath.VERSION_WEBHOOKS,
    query,
  }),
  versionWebhooksWebhook: (query: WebhookPageQuery) => ({
    pathname: ReferencePath.VERSION_WEBHOOKS_WEBHOOK,
    query,
  }),
  versionApi: (query: ApiPageQuery) => ({
    pathname: ReferencePath.VERSION_API,
    query,
  }),
  versionApiEndpoint: (query: ApiEndpointPageQuery) => ({
    pathname: ReferencePath.VERSION_API_ENDPOINT,
    query,
  }),
  versionApiWebhook: (query: ApiWebhookPageQuery) => ({
    pathname: ReferencePath.VERSION_API_WEBHOOK,
    query,
  }),
  versionApiWebhookIndex: (query: ApiPageQuery) => ({
    pathname: ReferencePath.VERSION_API_WEBHOOK_INDEX,
    query,
  }),
  versionSquareExplorerEndpoint: (query: ApiEndpointPageQuery) => ({
    pathname: ReferencePath.VERSION_EXPLORER_ENDPOINT,
    query,
  }),
};

function toUrl(referenceHrefBuilder: {
  pathname: ReferencePath;
  query:
    | SquareVersionPageQuery
    | ApiPageQuery
    | ApiEndpointPageQuery
    | ApiWebhookPageQuery
    | ObjectPageQuery
    | EnumPageQuery
    | WebhookPageQuery;
}): string {
  let url = referenceHrefBuilder.pathname.replace(
    '[square_version]',
    referenceHrefBuilder.query.square_version
  );

  if ('api' in referenceHrefBuilder.query) {
    url = url.replace('[api]', referenceHrefBuilder.query.api);
  }

  if ('endpoint' in referenceHrefBuilder.query) {
    url = url.replace('[endpoint]', referenceHrefBuilder.query.endpoint);
  }

  if ('webhook' in referenceHrefBuilder.query) {
    url = url.replace('[webhook]', referenceHrefBuilder.query.webhook);
  }

  if ('object' in referenceHrefBuilder.query) {
    url = url.replace('[object]', referenceHrefBuilder.query.object);
  }

  if ('enum' in referenceHrefBuilder.query) {
    url = url.replace('[enum]', referenceHrefBuilder.query.enum);
  }

  return url;
}

export {
  versionInfoToVersionPath,
  entityToIndex,
  toUrl,
  type SquareVersionPageQuery,
  type ApiPageQuery,
  type ApiEndpointPageQuery,
  type ApiWebhookPageQuery,
  ReferenceHrefBuilders,
  ReferencePath,
};
